import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ludus/ui'
import { SourceApiError } from '@/lib/api/sources.ts'

interface ConfirmSyncDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  onConfirm: () => Promise<unknown>
}

export function ConfirmSyncDialog({ open, onOpenChange, onConfirm }: ConfirmSyncDialogProps) {
  const { t } = useTranslation()
  const [starting, setStarting] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  async function confirm() {
    setStarting(true)
    setFailed(null)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch (err) {
      setFailed(
        err instanceof SourceApiError && err.detail !== null
          ? err.detail
          : t('pages.pipelines.sync.confirm.failed', {
              defaultValue: 'The sync could not be started. Please try again.',
            }),
      )
    } finally {
      setStarting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('pages.pipelines.sync.confirm.title', { defaultValue: 'Sync this pipeline?' })}
          </DialogTitle>
          <DialogDescription>
            {t('pages.pipelines.sync.confirm.description', {
              defaultValue:
                'Ludus reads the whole board, so a large one can take a while. The sync runs in the background — you can keep working, and closing this tab will not stop it.',
            })}
          </DialogDescription>
        </DialogHeader>
        {failed !== null && (
          <DialogBody className="pt-0">
            <p className="flex items-start gap-1.5 text-xs text-toast-error-text">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {failed}
            </p>
          </DialogBody>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={starting}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" size="sm" disabled={starting} onClick={() => void confirm()}>
            {starting
              ? t('pages.pipelines.sync.confirm.starting', { defaultValue: 'Starting…' })
              : t('pages.pipelines.sync.confirm.start', { defaultValue: 'Start sync' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
